import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchJobs, archiveJob, fetchChoices, fetchJob, getUserData } from '../api'; // Adjust the import path as necessary
import './joblist.css';
import JobModal from './jobmodal';
import NewJobModal from './newjobmodal';
import ContactFilter from './contactfilter';
import IncomingJobs from './IncomingJobs';
import { formatDateTimeDisplay, getTodayEastern, getTomorrowEastern, isTodayEastern, isTomorrowEastern, formatDateToEastern, formatDateDisplay } from '../../utils/timezone';
import { logComponent, logServiceWorker, logNavigation, logDiagnostic } from '../../utils/diagnosticLogger';

const JobList = () => {
  const navigate = useNavigate(); // Use React Router navigation instead of window.location
  const isMountedRef = useRef(true); // Track if component is mounted to prevent navigation after unmount
  const [jobs, setJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState([]); // Empty = all contacts
  const [defaultContact, setDefaultContact] = useState('');
  const [contactFilterReady, setContactFilterReady] = useState(false); // Wait for the user's default contact before the first load
  const latestJobsRequestRef = useRef(0); // Ignore responses from older filter selections
  const [refreshKey, setRefreshKey] = useState(0); // Bumped to reload the list after a change
  const refreshingJobsRef = useRef(false); // The next load is a refresh (keep the list if it fails)
  const [selectedService, setSelectedService] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [copyJobData, setCopyJobData] = useState(null);
  const [sortByDatePromised, setSortByDatePromised] = useState(false);
  const [reverseStatusOrder, setReverseStatusOrder] = useState(false);
  const [showBuffers, setShowBuffers] = useState(false);
  const [showColors, setShowColors] = useState(true);

  // (intentionally left without helper; using formatDateDisplay with year)
  const [isPhotoAlbumView, setIsPhotoAlbumView] = useState(window.innerWidth <= 1024);
  const [choices, setChoices] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [failedImages, setFailedImages] = useState(new Set()); // Track images that failed to load
  const [jobsToShow, setJobsToShow] = useState(50); // Number of jobs to display (start with 50)
  const [showLoadMore, setShowLoadMore] = useState(false); // Show Load More button only when scrolled to bottom
  const scrollContainerRef = useRef(null); // Ref for the scrollable container
  
  // Removed pagination state - now fetching all jobs

  // Cleanup on unmount
  useEffect(() => {
    // Ensure mounted flag is set to true on mount
    isMountedRef.current = true;
    
    logComponent('JobList', 'mount', {
      pathname: window.location.pathname,
      hash: window.location.hash,
      navigationType: performance.getEntriesByType('navigation')[0]?.type || 'unknown',
      timestamp: Date.now()
    });
    
    return () => {
      logComponent('JobList', 'unmount', {
        jobsCount: jobs.length,
        reason: 'component-unmounting',
        timestamp: Date.now()
      });
      isMountedRef.current = false;
    };
  }, []);

  // Detect window resize for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Function to format date and time in Eastern Time
  const formatDateTime = (dateStr, timeStr) => {
    if (!dateStr) return 'No Date';
    
    try {
      // On mobile, always show short format: MM/DD (no time, no year)
      if (isMobile) {
        const date = new Date(dateStr);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${month}/${day}`;
      }
      
      // Desktop: Use the timezone utility for consistent Eastern Time formatting
      if (timeStr) {
        // Combine date and time for proper timezone handling
        const dateTimeStr = `${dateStr}T${timeStr}`;
        return formatDateTimeDisplay(dateTimeStr);
      } else {
        return formatDateTimeDisplay(dateStr);
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };

  // Load choices from API
  useEffect(() => {
    const loadChoices = async () => {
      try {
        const choicesData = await fetchChoices();
        setChoices(choicesData);
      } catch (error) {
        console.error('Error loading choices:', error);
      }
    };
    loadChoices();
  }, []);

  // Start filtered to the user's default contact when they've turned that on in their profile
  useEffect(() => {
    const loadDefaultContactFilter = async () => {
      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('getUserData timeout after 5 seconds')), 5000)
        );
        const response = await Promise.race([getUserData(), timeoutPromise]);
        const { default_contact, filter_jobs_by_default_contact } = response.data || {};
        setDefaultContact(default_contact || '');
        if (default_contact && filter_jobs_by_default_contact) {
          setSelectedContacts([default_contact]);
        }
      } catch (error) {
        console.error('Error loading default contact filter:', error);
      } finally {
        setContactFilterReady(true);
      }
    };
    loadDefaultContactFilter();
  }, []);

  // Status order comes from the editable status choices (Choice Manager).
  // Any status on a job that isn't in that list is appended so those jobs still render.
  const getStatusOrderList = () => {
    const configured = [...(choices.status || [])]
      .sort((a, b) => a.order - b.order)
      .map(choice => choice.value);
    const extra = [...new Set(
      jobs.map(job => job.status).filter(status => status && !configured.includes(status))
    )];
    return [...configured, ...extra];
  };

  // Get the current status order based on reverseStatusOrder
  const getCurrentStatusOrder = () => {
    const order = getStatusOrderList();
    return reverseStatusOrder ? order.reverse() : order;
  };

  const toggleNewJobModal = () => {
    if (showNewJobModal) {
      setCopyJobData(null);
    }
    setShowNewJobModal(!showNewJobModal);
  };

  const handleCopyJob = (job) => {
    setCopyJobData(job);
    setShowNewJobModal(true);
  };

  const handleJobCreated = (newJob) => {
    // Newest first, like the server's order; the refresh then applies the filters
    setJobs(prevJobs => [newJob, ...prevJobs]);
    setShowNewJobModal(false);
    setCopyJobData(null);
    refreshJobs();
  };

  const changeContacts = (contacts) => {
    console.log('Changing contact filter to:', contacts);
    setSelectedContacts(contacts);
  };

  const toggleService = (service) => {
    console.log('Changing service filter to:', service);
    setSelectedService(service);
  };

  const toggleStatus = (status) => {
    console.log('Changing status filter to:', status);
    setSelectedStatus(status);
  };

  // Sort value for "Due" ordering; jobs with no due date sort after all dated jobs
  const getDatePromisedSortValue = (job) => {
    return job.date_promised ? new Date(job.date_promised).getTime() : Number.MAX_SAFE_INTEGER;
  };

  const toggleSortByDatePromised = () => {
    setSortByDatePromised(!sortByDatePromised);
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowModal(true);
  };

  const handleJobUpdate = (updatedJob) => {
    // If the job was archived, remove it from the list
    if (updatedJob.is_archived) {
      setJobs(prevJobs => prevJobs.filter(job => job.id !== updatedJob.id));
    } else {
      // Otherwise, update it in place
      setJobs(prevJobs => prevJobs.map(job => job.id === updatedJob.id ? updatedJob : job));
    }
    // Then take the list from the server: filters, invoice shading, and changes saved elsewhere
    refreshJobs();
  };

  const handleArchiveJob = async (jobId) => {
    try {
      await archiveJob(jobId);
      // Remove the archived job from the list
      setJobs(prevJobs => prevJobs.filter(job => job.id !== jobId));
      setShowModal(false);
    } catch (error) {
      console.error('Error archiving job:', error);
    }
  };

  // Removed pagination handlers - no longer needed

  const getRowClassName = (job) => {
    // Invoiced-pending-archive always takes priority regardless of showColors
    if (job.is_invoiced_pending_archive) return 'invoiced-pending-archive';
    if (!showColors) return '';
    if (!job.date_promised) return '';
    if (isTodayEastern(job.date_promised)) return 'due-today';
    // Check if overdue by comparing with today's date in Eastern Time
    const today = getTodayEastern();
    const promisedDate = formatDateToEastern(job.date_promised);
    if (promisedDate < today) return 'past-due';
    return '';
  };


  // Reload the list in place after a job is created or changed
  const refreshJobs = () => {
    refreshingJobsRef.current = true;
    setRefreshKey(key => key + 1);
  };

  useEffect(() => {
    if (!contactFilterReady) return;

    const requestId = ++latestJobsRequestRef.current;
    const isRefresh = refreshingJobsRef.current;
    refreshingJobsRef.current = false;

    const loadJobs = async () => {
      logDiagnostic('api', 'fetchJobs-start', {
        contact: selectedContacts,
        service: selectedService,
        search: searchQuery,
        status: selectedStatus,
        timestamp: Date.now()
      });
      
      console.log('Loading all jobs with filters:', {
        contact: selectedContacts,
        service: selectedService,
        search: searchQuery,
        status: selectedStatus
      });
      try {
        // Add timeout to detect if fetchJobs hangs
        const fetchPromise = fetchJobs(selectedContacts, selectedService, searchQuery, selectedStatus, 1, 10000);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('fetchJobs timeout after 30 seconds')), 30000)
        );
        
        // Fetch all jobs by setting a very high page size
        const response = await Promise.race([fetchPromise, timeoutPromise]);

        // A newer filter selection has started loading; drop this result
        if (requestId !== latestJobsRequestRef.current) return;
        
        logDiagnostic('api', 'fetchJobs-success', {
          jobsCount: response?.results?.length || 0,
          timestamp: Date.now()
        }, true); // Send immediately
        
        if (response && response.results) {
          // Ensure all job picture URLs are absolute
          const jobsWithAbsoluteUrls = response.results.map(job => ({
            ...job,
            picture: job.picture && !job.picture.startsWith('http') 
              ? `http://127.0.0.1:8000${job.picture}`
              : job.picture
          }));
          
          console.log('All jobs received:', jobsWithAbsoluteUrls.length);
          
          // Update state immediately - React will handle unmounted component updates safely
          // Check mounted status for logging but don't block the update
          const isMounted = isMountedRef.current;
          
          if (!isMounted) {
            logDiagnostic('warning', 'JobList-setting-jobs-unmounted', {
              jobsCount: jobsWithAbsoluteUrls.length,
              timestamp: Date.now()
            }, true);
          }
          
          logDiagnostic('component', 'JobList-setting-jobs', {
            jobsCount: jobsWithAbsoluteUrls.length,
            isMounted,
            timestamp: Date.now()
          }, true); // Send immediately
          
          try {
            setJobs(jobsWithAbsoluteUrls);
            // Clear failed images when jobs are reloaded (new images might be available)
            setFailedImages(new Set());
            
            logDiagnostic('component', 'JobList-jobs-set', {
              jobsCount: jobsWithAbsoluteUrls.length,
              timestamp: Date.now()
            }, true); // Send immediately
          } catch (setStateError) {
            logDiagnostic('error', 'JobList-setState-error', {
              error: setStateError.message,
              stack: setStateError.stack,
              jobsCount: jobsWithAbsoluteUrls.length,
              timestamp: Date.now()
            }, true); // Send immediately - critical
            throw setStateError; // Re-throw so error boundary can catch
          }
        } else {
          // Handle case where response doesn't have expected structure
          logDiagnostic('api', 'fetchJobs-no-results', {
            response: response ? 'exists' : 'null',
            timestamp: Date.now()
          });
          setJobs([]);
        }
      } catch (error) {
        if (requestId !== latestJobsRequestRef.current) return;
        console.error('Error loading jobs:', error);
        // A failed refresh keeps the jobs already shown
        if (isRefresh) return;
        logDiagnostic('api', 'fetchJobs-error', {
          error: error.message,
          status: error.response?.status,
          responseText: error.response?.statusText,
          stack: error.stack,
          url: window.location.href,
          pathname: window.location.pathname,
          timestamp: Date.now()
        });
        
        // Check if this is an auth error that might cause redirect
        if (error.response?.status === 401 || error.response?.status === 403) {
          logDiagnostic('api', 'fetchJobs-auth-error', {
            status: error.response.status,
            mightCauseReload: true,
            timestamp: Date.now()
          });
        }
        
        setJobs([]);
      }
    };

    loadJobs();
  }, [contactFilterReady, selectedContacts, selectedService, searchQuery, selectedStatus, refreshKey]);

  // Removed page reset effect - no longer needed with pagination removed

  // Check for selectedJobId from notification click - runs on component mount and when jobs change
  useEffect(() => {
    const selectedJobId = localStorage.getItem('selectedJobId');
    console.log('Checking for selectedJobId:', selectedJobId, 'Modal already open:', showModal);
    if (selectedJobId && !showModal) {
      // First try to find the job in the current jobs list
      const job = jobs.find(j => j.id === parseInt(selectedJobId));
      if (job) {
        console.log('Found job in current list, opening modal:', job.id);
        setSelectedJob(job);
        setShowModal(true);
        localStorage.removeItem('selectedJobId');
      } else if (jobs.length > 0) {
        // Job not in current filtered list, fetch it directly
        console.log('Job not in current list, fetching directly:', selectedJobId);
        fetchJob(parseInt(selectedJobId))
          .then(fetchedJob => {
            console.log('Fetched job:', fetchedJob);
            setSelectedJob(fetchedJob);
            setShowModal(true);
            localStorage.removeItem('selectedJobId');
          })
          .catch(error => {
            console.error('Error fetching job:', error);
            localStorage.removeItem('selectedJobId');
          });
      }
    }
  }, [jobs, showModal]);

  // Coordinated handler for jobSelected events (from all sources: service worker, hash, localStorage)
  // This prevents duplicate processing and race conditions
  useEffect(() => {
    // Track processing to prevent duplicate handling
    const processingJobs = new Set();
    
    const handleJobSelected = (event) => {
      const jobId = event.detail.jobId;
      if (!jobId) return;
      
      const jobIdNum = parseInt(jobId);
      if (isNaN(jobIdNum)) {
        console.warn('Invalid jobId in jobSelected event:', jobId);
        return;
      }
      
      // Prevent duplicate processing
      if (processingJobs.has(jobIdNum)) {
        console.log('Job', jobIdNum, 'already being processed, ignoring duplicate event');
        return;
      }
      
      // Prevent opening if modal is already open (unless it's a different job)
      if (showModal && selectedJob && selectedJob.id === jobIdNum) {
        console.log('Modal already open for job', jobIdNum, ', ignoring jobSelected event');
        return;
      }
      
      console.log('Processing jobSelected event for job ID:', jobIdNum, 'Current modal state:', showModal);
      
      // Mark as processing
      processingJobs.add(jobIdNum);
      
      // Clear localStorage to prevent duplicate opening
      localStorage.removeItem('selectedJobId');
      
      // Process the job selection
      const processJob = (fetchedJob) => {
        setSelectedJob(fetchedJob);
        setShowModal(true);
        // Remove from processing set after a delay to allow modal to open
        setTimeout(() => {
          processingJobs.delete(jobIdNum);
        }, 1000);
      };
      
      // If jobs are already loaded, try to find the job
      if (jobs.length > 0) {
        const job = jobs.find(j => j.id === jobIdNum);
        if (job) {
          console.log('Found job in loaded list, opening modal:', job.id);
          processJob(job);
        } else {
          // Fetch the job directly
          console.log('Job not in loaded list, fetching directly:', jobIdNum);
          fetchJob(jobIdNum)
            .then(fetchedJob => {
              console.log('Fetched job from event:', fetchedJob);
              processJob(fetchedJob);
            })
            .catch(error => {
              console.error('Error fetching job from event:', error);
              processingJobs.delete(jobIdNum);
            });
        }
      } else {
        // Jobs not loaded yet, fetch the job directly
        console.log('Jobs not loaded yet, fetching directly:', jobIdNum);
        fetchJob(jobIdNum)
          .then(fetchedJob => {
            console.log('Fetched job from event (no jobs loaded):', fetchedJob);
            processJob(fetchedJob);
          })
          .catch(error => {
            console.error('Error fetching job from event (no jobs loaded):', error);
            processingJobs.delete(jobIdNum);
          });
      }
    };

    // Add event listener
    window.addEventListener('jobSelected', handleJobSelected);

    // Cleanup
    return () => {
      window.removeEventListener('jobSelected', handleJobSelected);
      processingJobs.clear();
    };
  }, [jobs, showModal, selectedJob]); // Added selectedJob to dependencies

  // Listen for service worker messages (from push notification clicks)
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'OPEN_JOB') {
        const jobId = event.data.jobId;
        console.log('Received OPEN_JOB message from service worker:', jobId);
        
        logServiceWorker('message-received', {
          type: event.data.type,
          jobId: jobId,
          shouldNavigate: event.data.shouldNavigate,
          navigateTo: event.data.navigateTo,
          currentPath: window.location.pathname,
          isMounted: isMountedRef.current
        });
        
        // Check if component is still mounted before processing
        if (!isMountedRef.current) {
          console.log('Component unmounted, ignoring service worker message');
          logServiceWorker('message-ignored-unmounted', { jobId });
          return;
        }
        
        // Navigate if needed (user was on different page)
        // Use React Router navigate instead of window.location.href to avoid full page reload
        if (event.data.shouldNavigate && event.data.navigateTo) {
          // Extract path from URL (handles both full URLs and paths)
          // IMPORTANT: Remove hash to prevent double processing - hash handler will process it separately
          let targetPath = event.data.navigateTo;
          try {
            // If it's a full URL, extract the path WITHOUT hash
            const url = new URL(targetPath, window.location.origin);
            targetPath = url.pathname + url.search;  // Explicitly exclude hash
          } catch (e) {
            // If it's already a path (starts with /), use it as-is (remove hash if present)
            if (!targetPath.startsWith('/')) {
              targetPath = '/' + targetPath;
            }
            // Remove hash if present to prevent double processing
            const hashIndex = targetPath.indexOf('#');
            if (hashIndex !== -1) {
              targetPath = targetPath.substring(0, hashIndex);
            }
          }
          
          // Only navigate if component is still mounted
          if (isMountedRef.current) {
            try {
              logNavigation(window.location.pathname, targetPath, 'sw-message-navigate');
              navigate(targetPath);
            } catch (navError) {
              console.error('Navigation error:', navError);
              logDiagnostic('error', 'navigation-error', {
                error: navError.message,
                from: window.location.pathname,
                to: targetPath
              });
              // Don't throw - just log the error
            }
          }
          // Don't return here - let the jobId processing continue below
        }
        
        // Trigger the jobSelected event to reuse existing logic
        // This will be handled by the coordinated handler below
        if (jobId && isMountedRef.current) {
          window.dispatchEvent(new CustomEvent('jobSelected', { 
            detail: { jobId: jobId } 
          }));
        }
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    logServiceWorker('message-listener-added', {});

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
      logServiceWorker('message-listener-removed', {});
    };
  }, [navigate]); // Include navigate in dependencies (React Router's navigate is stable, but follows React rules)

  // Check for job_id in URL hash on component mount
  // This is a fallback for direct URL access with hash (legacy support)
  // Note: Service worker now uses messages instead of hash to avoid reloads
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#job_')) {
      const jobId = hash.replace('#job_', '');
      console.log('Found job_id in URL hash (legacy):', jobId);
      
      logDiagnostic('navigation', 'hash-found', {
        hash,
        jobId,
        pathname: window.location.pathname
      });
      
      // Clear the hash immediately using history API (doesn't cause reload)
      // This prevents the hash from being processed multiple times
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        logDiagnostic('navigation', 'hash-cleared', { jobId });
      }
      
      // Trigger the jobSelected event (will be handled by coordinated handler)
      // Add a small delay to ensure component is fully mounted
      setTimeout(() => {
        // Only process if component is still mounted
        if (isMountedRef.current) {
          window.dispatchEvent(new CustomEvent('jobSelected', { 
            detail: { jobId: jobId } 
          }));
        }
      }, 100);
    }
  }, []);

  // Track render performance
  const renderStartTimeRef = useRef(null);
  
  // Log before render to detect if render is being called
  useEffect(() => {
    logDiagnostic('component', 'JobList-render-attempt', {
      jobsCount: jobs.length,
      showModal,
      selectedJob: selectedJob?.id,
      timestamp: Date.now()
    }, true); // Send immediately
    
    // Log render completion time
    if (renderStartTimeRef.current) {
      const renderEndTime = performance.now();
      const renderDuration = renderEndTime - renderStartTimeRef.current;
      
      if (renderDuration > 100) { // Log if render takes more than 100ms
        logDiagnostic('component', 'JobList-render-slow', {
          jobsCount: jobs.length,
          renderDuration: Math.round(renderDuration),
          timestamp: Date.now()
        }, true);
      }
      
      logDiagnostic('component', 'JobList-render-complete', {
        jobsCount: jobs.length,
        renderDuration: Math.round(renderDuration),
        timestamp: Date.now()
      });
    }
  });

  // Log right before returning JSX
  renderStartTimeRef.current = performance.now();
  logDiagnostic('component', 'JobList-about-to-render', {
    jobsCount: jobs.length,
    isPhotoAlbumView,
    showBuffers,
    timestamp: Date.now()
  }, true); // Send immediately
  
  // Performance warning for large job lists
  if (jobs.length > 100) {
    logDiagnostic('component', 'JobList-large-render-warning', {
      jobsCount: jobs.length,
      warning: 'Rendering large number of jobs may cause performance issues',
      timestamp: Date.now()
    }, true);
  }
  
  // Safety limit: Only render limited number of jobs to prevent Safari crashes on mobile
  // Users can click "Load More" to see additional jobs
  // Always show at least the first 50 jobs (or all jobs if less than 50)
  const effectiveLimit = Math.max(50, jobsToShow);
  // When sorting by due date, sort the full list before applying the limit so the
  // earliest-due jobs are always in the first batch (jobs with no due date go last)
  const orderedJobs = sortByDatePromised
    ? [...jobs].sort((a, b) => getDatePromisedSortValue(a) - getDatePromisedSortValue(b))
    : jobs;
  const jobsToRender = orderedJobs.slice(0, effectiveLimit);
  const hasMoreJobs = jobs.length > effectiveLimit;

  // Handle scroll detection for Load More button (must be after hasMoreJobs is defined)
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !hasMoreJobs) {
      setShowLoadMore(false);
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Show button when scrolled within 300px of bottom, or if content fits in viewport
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom < 300;
      const fitsInViewport = scrollHeight <= clientHeight + 10; // All content visible (with small tolerance)
      setShowLoadMore(isNearBottom || fitsInViewport);
    };

    // Check initial position immediately
    handleScroll();

    // Also check after delays to account for content loading and rendering
    const timeoutIds = [
      setTimeout(handleScroll, 100),
      setTimeout(handleScroll, 500),
      setTimeout(handleScroll, 1000)
    ];

    scrollContainer.addEventListener('scroll', handleScroll);
    // Also listen for resize in case container size changes
    window.addEventListener('resize', handleScroll);
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [hasMoreJobs, jobsToShow, jobsToRender.length]);
  
  if (jobs.length > effectiveLimit) {
    logDiagnostic('component', 'JobList-render-limit-applied', {
      totalJobs: jobs.length,
      renderedJobs: jobsToRender.length,
      effectiveLimit,
      jobsToShow,
      warning: `Limiting render to first ${effectiveLimit} jobs to prevent Safari crashes`,
      timestamp: Date.now()
    }, true);
  }
  
  const handleLoadMore = () => {
    const newLimit = Math.min(jobsToShow + 50, jobs.length);
    setJobsToShow(newLimit);
    logDiagnostic('component', 'JobList-load-more', {
      previousLimit: jobsToShow,
      newLimit,
      totalJobs: jobs.length,
      timestamp: Date.now()
    }, true);
  };
  
  // Reset jobsToShow when jobs array changes significantly (e.g., filter applied)
  // But don't reset on initial load or when loading more jobs
  useEffect(() => {
    // Fix if jobsToShow is 0 but we have jobs (shouldn't happen, but ensure it works)
    if (jobsToShow === 0 && jobs.length > 0) {
      const newLimit = Math.min(50, jobs.length);
      setJobsToShow(newLimit);
      logDiagnostic('component', 'JobList-fix-zero-limit', {
        previousLimit: 0,
        newLimit,
        totalJobs: jobs.length,
        timestamp: Date.now()
      }, true);
    }
    // Only reset if jobs count decreased significantly (filter applied) - but NOT on initial load
    // We check if jobsToShow > 50 to ensure we're not resetting on initial load from 0 jobs
    else if (jobs.length > 0 && jobs.length < jobsToShow - 10 && jobsToShow > 50) {
      // Jobs decreased significantly AND we had more than 50 shown - this indicates a filter was applied
      const newLimit = Math.min(50, jobs.length);
      setJobsToShow(newLimit);
      logDiagnostic('component', 'JobList-reset-limit', {
        previousLimit: jobsToShow,
        newLimit,
        totalJobs: jobs.length,
        reason: 'filter applied - jobs decreased',
        timestamp: Date.now()
      }, true);
    }
  }, [jobs.length, jobsToShow]);
  
  // Detect if page is about to reload (Safari PWA behavior)
  useEffect(() => {
    const handleBeforeUnload = () => {
      logDiagnostic('navigation', 'page-before-unload', {
        jobsCount: jobs.length,
        pathname: window.location.pathname,
        timestamp: Date.now()
      }, true);
    };
    
    const handlePageHide = () => {
      logDiagnostic('navigation', 'page-hide', {
        jobsCount: jobs.length,
        pathname: window.location.pathname,
        timestamp: Date.now()
      }, true);
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [jobs.length]);
  
  // Safe map function that catches errors per job item
  const safeMapJobs = (jobsArray, renderFn, sectionName) => {
    if (!jobsArray || !Array.isArray(jobsArray)) {
      logDiagnostic('error', 'JobList-invalid-jobs-array', {
        section: sectionName,
        jobsType: typeof jobsArray,
        timestamp: Date.now()
      }, true);
      return [];
    }

    logDiagnostic('component', 'JobList-safeMapJobs-start', {
      section: sectionName,
      jobsCount: jobsArray.length,
      timestamp: Date.now()
    }, true);

    const result = jobsArray.map((job, index) => {
      try {
        if (!job || !job.id) {
          logDiagnostic('error', 'JobList-invalid-job', {
            section: sectionName,
            index,
            job: job ? JSON.stringify(job).substring(0, 100) : 'null',
            timestamp: Date.now()
          }, true);
          return null;
        }
        return renderFn(job, index);
      } catch (jobError) {
        logDiagnostic('error', 'JobList-render-job-error', {
          section: sectionName,
          jobId: job?.id || 'unknown',
          index,
          error: jobError.message,
          stack: jobError.stack?.substring(0, 200),
          timestamp: Date.now()
        }, true);
        return null; // Skip this job instead of crashing
      }
    }).filter(item => item !== null); // Remove null entries
    
    logDiagnostic('component', 'JobList-safeMapJobs-complete', {
      section: sectionName,
      inputCount: jobsArray.length,
      outputCount: result.length,
      timestamp: Date.now()
    }, true);
    
    return result;
  };
  
  return (
    <div className='joblist'>
      <div className='joblistfilters'>
        <button className='joblistnewjobbutton' onClick={toggleNewJobModal}>
          <span className='joblistbuttontext'>New Job</span>
          <span className='joblistbuttonplus'>+</span>
        </button>
        <div className='joblistfiltersrow'>
          <div className='jobliststatusfilter'>
            <label className='jobliststatuslabel'>
              <span className='joblistlabeltext'>Status</span>
              <select className='jobliststatusselect' value={selectedStatus} onChange={(e) => toggleStatus(e.target.value)}>
                <option value="all">All statuses</option>
                {choices.status && choices.status
                  .sort((a, b) => a.order - b.order)
                  .map(choice => (
                    <option key={choice.value} value={choice.value}>
                      {choice.display_name}
                    </option>
                  ))}
              </select>
            </label>
          </div>
          
          <div className='jobliststatusfilter'>
            <div className='jobliststatuslabel'>
              <span className='joblistlabeltext'>Contact</span>
              <ContactFilter
                contactChoices={choices.contact || []}
                selectedContacts={selectedContacts}
                onChange={changeContacts}
                defaultContact={defaultContact}
              />
            </div>
          </div>

          <div className='jobliststatusfilter'>
            <label className='jobliststatuslabel'>
              <span className='joblistlabeltext'>Service</span>
              <select className='jobliststatusselect' value={selectedService} onChange={(e) => toggleService(e.target.value)}>
                <option value="">All services</option>
                {choices.service && choices.service
                  .sort((a, b) => a.order - b.order)
                  .map(choice => (
                    <option key={choice.value} value={choice.value}>
                      {choice.display_name}
                    </option>
                  ))}
              </select>
            </label>
          </div>

          <span className='joblistfilterdivider' aria-hidden='true' />

          <div className='joblistviewbuttons'>
            <button
              className={`joblistsortButton ${sortByDatePromised ? 'active' : ''}`}
              onClick={toggleSortByDatePromised}
              aria-pressed={sortByDatePromised}
              title='Sort by due date'
            >
              Due
            </button>

            <button
              className='joblistsortButton'
              onClick={() => setReverseStatusOrder(!reverseStatusOrder)}
              title='Reverse the status order'
            >
              {reverseStatusOrder ? 'Status ↑' : 'Status ↓'}
            </button>

            <button
              className={`joblistsortButton ${showBuffers ? 'active' : ''}`}
              onClick={() => setShowBuffers(!showBuffers)}
              aria-pressed={showBuffers}
              title='Group jobs by status'
            >
              Stations
            </button>

            <button
              className={`joblistsortButton ${showColors ? 'active' : ''}`}
              onClick={() => setShowColors(!showColors)}
              aria-pressed={showColors}
              title='Highlight due today and past due jobs'
            >
              Colors
            </button>

            <button
              className={`joblistsortButton ${isPhotoAlbumView ? 'active' : ''}`}
              onClick={() => setIsPhotoAlbumView(!isPhotoAlbumView)}
              aria-pressed={isPhotoAlbumView}
              title='Show jobs as a photo album'
            >
              Photo Album
            </button>
          </div>
        </div>
        <label className='jobliststatuslabel joblistsearchlabel'>
          <span className='joblistlabeltext'>Search</span>
          <input
            className='joblistsearchbar'
            type="text"
            placeholder="⌕ Search Jobs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>
      </div>
      <button className='joblistmobilebutton' onClick={toggleNewJobModal}>
        <span className='joblistbuttonplus'>+</span>
      </button>

      <div className='joblistlabelsparent'>
        <div className='joblistlabels'>
          <h5 className='joblistlabelsheaders'>Photo</h5>
          <h5 className='joblistlabelsheaders'>Contact</h5>
          <h5 className='joblistlabelsheaders'>Qty</h5>
          <h5 className='joblistlabelsheaders'>Make</h5>
          <h5 className='joblistlabelsheaders'>Service</h5>
          <h5 className='joblistlabelsheaders'>Due</h5>
          <h5 className='joblistlabelsheaders'>Customer</h5>
          <h5 className='joblistlabelsheaders'>Created</h5>
          <h5 className='joblistlabelsheaders'>Job #</h5>
        </div>
        <hr className='joblistlabelsheadersline'/>
      </div>

      <div className='joblistscroll' ref={scrollContainerRef}>
        <IncomingJobs
          defaultContact={defaultContact}
          search={searchQuery}
          ready={contactFilterReady}
          refreshKey={refreshKey}
          onOpen={handleJobClick}
          onReceived={() => setRefreshKey((key) => key + 1)}
        />
        {jobs && jobs.length > 0 ? (
          <>
            {isPhotoAlbumView ? (
              <div className='photoalbumview'>
                {showBuffers ? (
                  <>
                    {/* Show jobs with no status first */}
                    {(() => {
                      const noStatusJobs = jobsToRender.filter(job => !job.status);
                      if (noStatusJobs.length > 0) {
                        return (
                          <React.Fragment>
                            <div className='photoalbumstatusbuffer'>
                              <div className='photoalbumstatusbuffertext'>No Status</div>
                            </div>
                            <div className='photoalbumgrid'>
                              {safeMapJobs(
                                noStatusJobs
                                  .sort((a, b) => {
                                    try {
                                      if (sortByDatePromised) {
                                        const dateA = getDatePromisedSortValue(a);
                                        const dateB = getDatePromisedSortValue(b);
                                        return dateA - dateB;
                                      }
                                      const dateA = a.date_created ? new Date(a.date_created) : new Date(0);
                                      const dateB = b.date_created ? new Date(b.date_created) : new Date(0);
                                      return dateB - dateA;
                                    } catch (sortError) {
                                      logDiagnostic('error', 'JobList-sort-error', {
                                        jobA: a?.id,
                                        jobB: b?.id,
                                        error: sortError.message,
                                        timestamp: Date.now()
                                      }, true);
                                      return 0;
                                    }
                                  }),
                                (job) => (
                                  <div 
                                    key={job.id}
                                    className={`photoalbumitem ${getRowClassName(job)}`}
                                    onClick={() => handleJobClick(job)}
                                  >
                                    <div className='photoalbumphoto'>
                                      {job.picture && !failedImages.has(job.id) ? (
                                        <img 
                                          src={job.picture} 
                                          alt={`Job ${job.id}`} 
                                          className='photoalbumimg'
                                          onError={(e) => {
                                            // Mark image as failed to prevent re-rendering attempts
                                            e.target.style.display = 'none';
                                            setFailedImages(prev => new Set([...prev, job.id]));
                                          }}
                                          loading="lazy"
                                        />
                                      ) : (
                                        <div className='photoalbumnophoto'>No Photo</div>
                                      )}
                                    </div>
                                    <div className='photoalbumdetails'>
                                      <div className='photoalbumjobid'>Job #{job.id}</div>
                                      <div className='photoalbumcustomer'>{job.customer_name || job.customer || 'No Customer'}</div>
                                      <div className='photoalbumcontact'>Contact: {job.contact || '-'}</div>
                                      <div className='photoalbumquantity'>Qty: {job.quantity || 'N/A'}</div>
                                      <div className='photoalbumservice'>{job.service || 'No Service'}</div>
                                      <div className='photoalbumdatetime'>
                                        {formatDateTime(job.date_promised, job.time_promised)}
                                      </div>
                                    </div>
                                  </div>
                                ),
                                'no-status-photo-album'
                              )}
                            </div>
                          </React.Fragment>
                        );
                      }
                      return null;
                    })()}

                    {/* Show jobs with statuses */}
                    {getCurrentStatusOrder().map(status => {
                      const statusJobs = jobsToRender.filter(job => job.status === status);
                      if (statusJobs.length === 0) return null;

                      return (
                        <React.Fragment key={status}>
                          <div className='photoalbumstatusbuffer'>
                            <div className='photoalbumstatusbuffertext'>{status}</div>
                          </div>
                          <div className='photoalbumgrid'>
                            {safeMapJobs(
                              statusJobs
                                .sort((a, b) => {
                                  try {
                                    if (sortByDatePromised) {
                                      const dateA = getDatePromisedSortValue(a);
                                      const dateB = getDatePromisedSortValue(b);
                                      return dateA - dateB;
                                    }
                                    const dateA = a.date_created ? new Date(a.date_created) : new Date(0);
                                    const dateB = b.date_created ? new Date(b.date_created) : new Date(0);
                                    return dateB - dateA;
                                  } catch (sortError) {
                                    logDiagnostic('error', 'JobList-sort-error', {
                                      jobA: a?.id,
                                      jobB: b?.id,
                                      error: sortError.message,
                                      timestamp: Date.now()
                                    }, true);
                                    return 0;
                                  }
                                }),
                              (job) => (
                                <div 
                                  key={job.id}
                                  className={`photoalbumitem ${getRowClassName(job)}`}
                                  onClick={() => handleJobClick(job)}
                                >
                                  <div className='photoalbumphoto'>
                                    {job.picture ? (
                                      <img 
                                        src={job.picture} 
                                        alt={`Job ${job.id}`} 
                                        className='photoalbumimg'
                                      />
                                    ) : (
                                      <div className='photoalbumnophoto'>No Photo</div>
                                    )}
                                  </div>
                                  <div className='photoalbumdetails'>
                                    <div className='photoalbumjobid'>Job #{job.id}</div>
                                    <div className='photoalbumcustomer'>{job.customer_name || job.customer || 'No Customer'}</div>
                                    <div className='photoalbumcontact'>Contact: {job.contact || '-'}</div>
                                    <div className='photoalbumquantity'>Qty: {job.quantity || 'N/A'}</div>
                                    <div className='photoalbumservice'>{job.service || 'No Service'}</div>
                                    <div className='photoalbumdatetime'>
                                      {formatDateTime(job.date_promised, job.time_promised)}
                                    </div>
                                  </div>
                                </div>
                              ),
                              `status-${status}-photo-album`
                            )}
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  // Flat photo grid view
                  <div className='photoalbumgrid'>
                    {safeMapJobs(
                      jobsToRender
                        .sort((a, b) => {
                          try {
                            if (sortByDatePromised) {
                              const dateA = getDatePromisedSortValue(a);
                              const dateB = getDatePromisedSortValue(b);
                              return dateA - dateB;
                            }
                            const dateA = a.date_created ? new Date(a.date_created) : new Date(0);
                            const dateB = b.date_created ? new Date(b.date_created) : new Date(0);
                            return dateB - dateA;
                          } catch (sortError) {
                            logDiagnostic('error', 'JobList-sort-error', {
                              jobA: a?.id,
                              jobB: b?.id,
                              error: sortError.message,
                              timestamp: Date.now()
                            }, true);
                            return 0;
                          }
                        }),
                      (job) => (
                        <div 
                          key={job.id}
                          className={`photoalbumitem ${getRowClassName(job)}`}
                          onClick={() => handleJobClick(job)}
                        >
                          <div className='photoalbumphoto'>
                            {job.picture ? (
                              <img 
                                src={job.picture} 
                                alt={`Job ${job.id}`} 
                                className='photoalbumimg'
                              />
                            ) : (
                              <div className='photoalbumnophoto'>No Photo</div>
                            )}
                          </div>
                          <div className='photoalbumdetails'>
                            <div className='photoalbumjobid'>Job #{job.id}</div>
                            <div className='photoalbumcustomer'>{job.customer_name || job.customer || 'No Customer'}</div>
                            <div className='photoalbumcontact'>Contact: {job.contact || '-'}</div>
                            <div className='photoalbumquantity'>Qty: {job.quantity || 'N/A'}</div>
                            <div className='photoalbumservice'>{job.service || 'No Service'}</div>
                            <div className='photoalbumdatetime'>
                              {formatDateTime(job.date_promised, job.time_promised)}
                            </div>
                          </div>
                        </div>
                      ),
                      'flat-photo-album'
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                {showBuffers ? (
                  <>
                    {/* Show jobs with no status first */}
                    {(() => {
                      const noStatusJobs = jobsToRender.filter(job => !job.status);
                      if (noStatusJobs.length > 0) {
                        return (
                          <React.Fragment>
                            <div className='statusbuffer'>
                              <div className='statusbuffertext'>No Status</div>
                            </div>
                            {safeMapJobs(
                              noStatusJobs
                                .sort((a, b) => {
                                  try {
                                    if (sortByDatePromised) {
                                      const dateA = getDatePromisedSortValue(a);
                                      const dateB = getDatePromisedSortValue(b);
                                      return dateA - dateB;
                                    }
                                    const dateA = a.date_created ? new Date(a.date_created) : new Date(0);
                                    const dateB = b.date_created ? new Date(b.date_created) : new Date(0);
                                    return dateB - dateA;
                                  } catch (sortError) {
                                    logDiagnostic('error', 'JobList-sort-error', {
                                      jobA: a?.id,
                                      jobB: b?.id,
                                      error: sortError.message,
                                      timestamp: Date.now()
                                    }, true);
                                    return 0;
                                  }
                                }),
                              (job) => (
                                <div 
                                  className={`joblistrow ${getRowClassName(job)}`} 
                                  key={job.id} 
                                  onClick={() => handleJobClick(job)}
                                >
                                  <div className='joblistrowdata joblistphoto'>
                                    {job.picture && !failedImages.has(job.id) ? (
                                      <img 
                                        src={job.picture} 
                                        alt={`Job ${job.id}`} 
                                        className='joblistphotoimg'
                                        onError={(e) => {
                                          // Mark image as failed to prevent re-rendering attempts
                                          e.target.style.display = 'none';
                                          setFailedImages(prev => new Set([...prev, job.id]));
                                        }}
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className='joblistnophoto'>No Photo</div>
                                    )}
                                  </div>
                                  <div className='joblistrowdata'>{job.contact || '-'}</div>
                                  <div className='joblistrowdata'>{job.quantity || 'N/A'}</div>
                                  <div className='joblistrowdata'>{job.make || 'No Make'}</div>
                                  <div className='joblistrowdata'>{job.service || 'No Service'}</div>
                                  <div className='joblistrowdata'>{formatDateTime(job.date_promised, job.time_promised)}</div>
                                  <div className='joblistrowdata'>{job.customer_name || job.customer || 'No Customer'}</div>
                                  <div className='joblistrowdata'>{formatDateDisplay(job.date_created)}</div>
                                  <div className='joblistrowdata'>{job.id}</div>
                                </div>
                              ))}
                          </React.Fragment>
                        );
                      }
                      return null;
                    })()}

                    {/* Show jobs with statuses */}
                    {getCurrentStatusOrder().map(status => {
                      const statusJobs = jobsToRender.filter(job => job.status === status);
                      if (statusJobs.length === 0) return null;

                      return (
                        <React.Fragment key={status}>
                          <div className='statusbuffer'>
                            <div className='statusbuffertext'>{status}</div>
                          </div>
                          {safeMapJobs(
                            statusJobs
                              .sort((a, b) => {
                                try {
                                  if (sortByDatePromised) {
                                    const dateA = getDatePromisedSortValue(a);
                                    const dateB = getDatePromisedSortValue(b);
                                    return dateA - dateB;
                                  }
                                  const dateA = a.date_created ? new Date(a.date_created) : new Date(0);
                                  const dateB = b.date_created ? new Date(b.date_created) : new Date(0);
                                  return dateB - dateA;
                                } catch (sortError) {
                                  logDiagnostic('error', 'JobList-sort-error', {
                                    jobA: a?.id,
                                    jobB: b?.id,
                                    error: sortError.message,
                                    timestamp: Date.now()
                                  }, true);
                                  return 0;
                                }
                              }),
                            (job) => (
                              <div 
                                className={`joblistrow ${getRowClassName(job)}`} 
                                key={job.id} 
                                onClick={() => handleJobClick(job)}
                              >
                                <div className='joblistrowdata joblistphoto'>
                                  {job.picture ? (
                                    <img 
                                      src={job.picture} 
                                      alt={`Job ${job.id}`} 
                                      className='joblistphotoimg'
                                    />
                                  ) : (
                                    <div className='joblistnophoto'>No Photo</div>
                                  )}
                                </div>
                                <div className='joblistrowdata'>{job.contact || '-'}</div>
                                <div className='joblistrowdata'>{job.quantity || 'N/A'}</div>
                                <div className='joblistrowdata'>{job.make || 'No Make'}</div>
                                <div className='joblistrowdata'>{job.service || 'No Service'}</div>
                                <div className='joblistrowdata'>{formatDateTime(job.date_promised, job.time_promised)}</div>
                                <div className='joblistrowdata'>{job.customer_name || job.customer || 'No Customer'}</div>
                                <div className='joblistrowdata'>{formatDateDisplay(job.date_created)}</div>
                                <div className='joblistrowdata'>{job.id}</div>
                              </div>
                            ),
                            `status-${status}-list`
                          )}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  // Flat list view
                  (() => {
                    const sortedJobs = [...jobsToRender].sort((a, b) => {
                      try {
                        if (sortByDatePromised) {
                          const dateA = getDatePromisedSortValue(a);
                          const dateB = getDatePromisedSortValue(b);
                          return dateA - dateB;
                        }
                        const dateA = a.date_created ? new Date(a.date_created) : new Date(0);
                        const dateB = b.date_created ? new Date(b.date_created) : new Date(0);
                        return dateB - dateA;
                      } catch (sortError) {
                        logDiagnostic('error', 'JobList-sort-error', {
                          jobA: a?.id,
                          jobB: b?.id,
                          error: sortError.message,
                          timestamp: Date.now()
                        }, true);
                        return 0;
                      }
                    });
                    return safeMapJobs(
                      sortedJobs,
                      (job) => (
                        <div 
                          className={`joblistrow ${getRowClassName(job)}`}
                          key={job.id} 
                          onClick={() => handleJobClick(job)}
                        >
                          <div className='joblistrowdata joblistphoto'>
                            {job.picture ? (
                              <img 
                                src={job.picture} 
                                alt={`Job ${job.id}`} 
                                className='joblistphotoimg'
                              />
                            ) : (
                              <div className='joblistnophoto'>No Photo</div>
                            )}
                          </div>
                          <div className='joblistrowdata'>{job.contact || '-'}</div>
                          <div className='joblistrowdata'>{job.quantity || 'N/A'}</div>
                          <div className='joblistrowdata'>{job.make || 'No Make'}</div>
                          <div className='joblistrowdata'>{job.service || 'No Service'}</div>
                          <div className='joblistrowdata'>{formatDateTime(job.date_promised, job.time_promised)}</div>
                          <div className='joblistrowdata'>{job.customer_name || job.customer || 'No Customer'}</div>
                          <div className='joblistrowdata'>{formatDateDisplay(job.date_created)}</div>
                          <div className='joblistrowdata'>{job.id}</div>
                        </div>
                      ),
                      'flat-list'
                    );
                  })()
                )}
              </>
            )}
          </>
        ) : (
          <p>No jobs found</p>
        )}
        
        {/* Load More button - shown when scrolled to bottom or if all content fits */}
        {hasMoreJobs && (
          <div 
            style={{ 
              textAlign: 'center', 
              padding: '20px 10px',
              backgroundColor: '#fff',
              borderTop: '1px solid #ddd',
              marginTop: '10px',
              display: showLoadMore ? 'block' : 'none'
            }}
          >
            <button
              onClick={handleLoadMore}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                backgroundColor: '#1976d2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold',
                width: '100%',
                maxWidth: '300px'
              }}
            >
              Load More ({jobs.length - jobsToRender.length} remaining)
            </button>
          </div>
        )}
      </div>

      <NewJobModal
        showModal={showNewJobModal}
        toggleModal={toggleNewJobModal}
        onJobCreated={handleJobCreated}
        initialData={copyJobData}
      />
      <JobModal
        showModal={showModal}
        toggleModal={toggleModal}
        job={selectedJob}
        onJobUpdated={handleJobUpdate}
        onArchiveJob={handleArchiveJob}
        onCopyJob={handleCopyJob}
      />
    </div>
  );
};

export default JobList;
